import { useEffect, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Plus, History, Loader2 } from "lucide-react";
import { useDashboardContext } from "@/components/DashboardLayout";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { AverageScoreOverview } from "@/components/AverageScoreOverview";
import type { AnalysisResult } from "@/types/analysis";
import { usePageMeta } from "@/hooks/usePageMeta";

export default function DashboardPage() {
  const { user } = useAuth();
  const { openNewAnalysis } = useDashboardContext();
  const navigate = useNavigate();
  usePageMeta({ title: "Dashboard — Unranked" });
  const [results, setResults] = useState<AnalysisResult[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    (async () => {
      const { data } = await supabase
        .from("analyses")
        .select("results")
        .order("created_at", { ascending: false });
      if (data) {
        setResults(data.map((row) => row.results as unknown as AnalysisResult));
      }
      setLoading(false);
    })();
  }, [user]);

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground font-display">Dashboard</h1>
          <p className="text-muted-foreground text-sm mt-1">Welcome back, {user?.email}</p>
        </div>
        <Button onClick={openNewAnalysis} className="gap-2">
          <Plus className="h-4 w-4" />
          New Analysis
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : results.length > 0 ? (
        <AverageScoreOverview results={results} />
      ) : null}

      <div className="grid gap-4 sm:grid-cols-2">
        <button
          onClick={openNewAnalysis}
          className="rounded-lg border border-border bg-card p-6 text-left hover:bg-muted/50 transition-colors"
        >
          <Plus className="h-5 w-5 text-primary mb-2" />
          <p className="text-sm font-medium text-foreground">Run a new analysis</p>
          <p className="text-xs text-muted-foreground mt-1">Enter a URL to get a full SEO report.</p>
        </button>
        <button
          onClick={() => navigate("/analyses")}
          className="rounded-lg border border-border bg-card p-6 text-left hover:bg-muted/50 transition-colors"
        >
          <History className="h-5 w-5 text-primary mb-2" />
          <p className="text-sm font-medium text-foreground">Previous analyses</p>
          <p className="text-xs text-muted-foreground mt-1">Revisit your past SEO reports.</p>
        </button>
      </div>
    </div>
  );
}
